import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  BrowseStageCounts,
  CandidateBrowseFacets,
  CandidateBrowseRow,
  CandidateBulkRequest,
  CandidateBulkResult,
  CandidateProfile,
  CandidateTag,
  CandidateTagInput,
  TagOption,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost, apiPut, request } from '../../../lib/api-client';
import { CANDIDATES_KEY, candidateKey } from './candidates-api';

// Redesign Group 1: All candidates (with Advanced search folded in), the stage tiles, the counts
// beside every filter option, the per-person star and candidate tags. Every key sits under
// `['candidates']`, so the server's realtime hints and a colleague's change refresh them.

const BROWSE_KEY = [...CANDIDATES_KEY, 'browse'] as const;
const FACETS_KEY = [...CANDIDATES_KEY, 'facets'] as const;
const STAGE_COUNTS_KEY = [...CANDIDATES_KEY, 'stage-counts'] as const;
const TAGS_KEY = ['tags'] as const;

/** The list's filters exactly as they sit in the address (06-qol §6): lists are comma-joined. */
export type BrowseParams = Readonly<Record<string, string | number | undefined>>;

export interface BrowsePage {
  readonly rows: readonly CandidateBrowseRow[];
  readonly total: number;
}

export function useCandidateBrowse(params: BrowseParams): UseQueryResult<BrowsePage, ApiError> {
  return useQuery<BrowsePage, ApiError>({
    queryKey: [...BROWSE_KEY, params],
    queryFn: async ({ signal }) => {
      const result = await request<readonly CandidateBrowseRow[]>('/candidates/browse', { query: params, signal });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export function useCandidateFacets(params: BrowseParams): UseQueryResult<CandidateBrowseFacets, ApiError> {
  return useQuery<CandidateBrowseFacets, ApiError>({
    queryKey: [...FACETS_KEY, params],
    queryFn: ({ signal }) => request<CandidateBrowseFacets>('/candidates/browse/facets', { query: params, signal }).then((result) => result.data),
    placeholderData: (previous) => previous,
  });
}

export function useStageCounts(): UseQueryResult<BrowseStageCounts, ApiError> {
  return useQuery<BrowseStageCounts, ApiError>({
    queryKey: STAGE_COUNTS_KEY,
    queryFn: () => apiGet<BrowseStageCounts>('/candidates/stage-counts'),
    placeholderData: (previous) => previous,
  });
}

/** Every tag, for the tag pickers. */
export function useTagOptions(isEnabled = true): UseQueryResult<readonly TagOption[], ApiError> {
  return useQuery<readonly TagOption[], ApiError>({
    queryKey: TAGS_KEY,
    queryFn: () => apiGet<readonly TagOption[]>('/tags'),
    enabled: isEnabled,
    staleTime: 60_000,
  });
}

// --- The star ------------------------------------------------------------------------------------

/**
 * Star or unstar one person, for the viewer only. The star flips at once in every list row and
 * in the profile; a refusal flips it back and the caller toasts why (§5a rule 1).
 */
export function useToggleStar() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { readonly candidateId: string; readonly isStarred: boolean }, { readonly snapshot: Snapshot }>({
    mutationFn: ({ candidateId, isStarred }) =>
      isStarred ? apiPut(`/candidates/${candidateId}/star`) : apiDelete(`/candidates/${candidateId}/star`),
    onMutate: async ({ candidateId, isStarred }) => {
      const snapshot = await takeSnapshot(queryClient, candidateId);
      patchRows(queryClient, (row) => (row.candidate.id === candidateId ? { ...row, isStarred } : row));
      queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), (profile) => (profile ? { ...profile, isStarred } : profile));
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
    // Hold the optimistic state until the refetch lands (§5a rule 3).
    onSettled: () => queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }),
  });
}

// --- Tags ------------------------------------------------------------------------------------------

export function useAddCandidateTag() {
  const queryClient = useQueryClient();
  return useMutation<CandidateTag, ApiError, { readonly candidateId: string; readonly input: CandidateTagInput; readonly optimistic: CandidateTag }, { readonly snapshot: Snapshot }>({
    mutationFn: ({ candidateId, input }) => apiPost<CandidateTag>(`/candidates/${candidateId}/tags`, input),
    onMutate: async ({ candidateId, optimistic }) => {
      const snapshot = await takeSnapshot(queryClient, candidateId);
      const add = (tags: readonly CandidateTag[]): readonly CandidateTag[] =>
        tags.some((tag) => tag.name.toLowerCase() === optimistic.name.toLowerCase()) ? tags : [...tags, optimistic];
      patchRows(queryClient, (row) => (row.candidate.id === candidateId ? { ...row, tags: add(row.tags) } : row));
      queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), (profile) => (profile ? { ...profile, tags: add(profile.tags) } : profile));
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
    onSettled: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }), queryClient.invalidateQueries({ queryKey: TAGS_KEY })]);
    },
  });
}

export function useRemoveCandidateTag() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { readonly candidateId: string; readonly tagId: string }, { readonly snapshot: Snapshot }>({
    mutationFn: ({ candidateId, tagId }) => apiDelete(`/candidates/${candidateId}/tags/${tagId}`),
    onMutate: async ({ candidateId, tagId }) => {
      const snapshot = await takeSnapshot(queryClient, candidateId);
      const drop = (tags: readonly CandidateTag[]): readonly CandidateTag[] => tags.filter((tag) => tag.id !== tagId);
      patchRows(queryClient, (row) => (row.candidate.id === candidateId ? { ...row, tags: drop(row.tags) } : row));
      queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), (profile) => (profile ? { ...profile, tags: drop(profile.tags) } : profile));
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }),
  });
}

// --- Bulk ------------------------------------------------------------------------------------------

/** Star, unstar, tag, untag or email ticked people, or everyone matching ("Select all N matching"). */
export function useCandidateBulk() {
  const queryClient = useQueryClient();
  return useMutation<CandidateBulkResult, ApiError, CandidateBulkRequest>({
    mutationFn: (input) => apiPost<CandidateBulkResult>('/candidates/bulk', input),
    onSettled: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY }), queryClient.invalidateQueries({ queryKey: TAGS_KEY })]);
    },
  });
}

// --- Optimistic helpers ----------------------------------------------------------------------------

type Snapshot = readonly (readonly [readonly unknown[], unknown])[];

async function takeSnapshot(queryClient: ReturnType<typeof useQueryClient>, candidateId: string): Promise<Snapshot> {
  await Promise.all([queryClient.cancelQueries({ queryKey: BROWSE_KEY }), queryClient.cancelQueries({ queryKey: candidateKey(candidateId) })]);
  return [...queryClient.getQueriesData({ queryKey: BROWSE_KEY }), [candidateKey(candidateId), queryClient.getQueryData(candidateKey(candidateId))]];
}

function restoreSnapshot(queryClient: ReturnType<typeof useQueryClient>, snapshot: Snapshot): void {
  for (const [key, data] of snapshot) queryClient.setQueryData(key, data);
}

function patchRows(queryClient: ReturnType<typeof useQueryClient>, patch: (row: CandidateBrowseRow) => CandidateBrowseRow): void {
  queryClient.setQueriesData<BrowsePage>({ queryKey: BROWSE_KEY }, (page) => (page ? { ...page, rows: page.rows.map(patch) } : page));
}
